const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[6-9]\d{9}$/;
const NON_NEGATIVE_NUMBER_REGEX = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;
const PLACEHOLDER_NOISE_REGEX =
  /^(?:test|n\/?a|na|null|undefined|sdfghj|sdfghjhgjhg|jhgjh|asdfg?h?|qwerty|lorem\s+ipsum|placeholder(?:\s+text)?)$/i;
const LINKEDIN_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?linkedin\.com\/(?:in|pub)\/[a-z0-9][a-z0-9._%+-]*(?:\/)?$/i;
const GITHUB_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?github\.com\/[a-z0-9](?:[a-z0-9-]{0,37}[a-z0-9])?(?:\/)?$/i;

export function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

export function stripHtml(value = "") {
  if (!hasText(value)) return "";

  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function getTextList(values) {
  if (!Array.isArray(values)) return [];

  return values
    .filter((value) => typeof value === "string")
    .map((value) => value.trim());
}

function getFilledTextList(values) {
  return getTextList(values).filter(Boolean);
}

function getEmptyTextSlots(values) {
  return getTextList(values).filter((value) => !value);
}

function hasStartedExperienceEntry(experience) {
  const descriptionText = stripHtml(experience?.description || "");

  return (
    hasText(experience?.company) ||
    hasText(experience?.role) ||
    hasText(experience?.project) ||
    hasText(experience?.years) ||
    hasText(experience?.startDate) ||
    hasText(experience?.endDate) ||
    hasText(descriptionText)
  );
}

function isCompleteExperienceEntry(experience) {
  const descriptionText = stripHtml(experience?.description || "");
  const hasTimeline =
    hasText(experience?.years) ||
    hasText(experience?.startDate) ||
    hasText(experience?.endDate);

  return (
    hasText(experience?.company) &&
    hasText(experience?.role) &&
    hasTimeline &&
    hasText(descriptionText)
  );
}

function hasStartedEducationEntry(education) {
  return (
    hasText(education?.institution) ||
    hasText(education?.url) ||
    hasText(education?.degree) ||
    hasText(education?.field) ||
    hasText(education?.from) ||
    hasText(education?.to) ||
    hasText(education?.score)
  );
}

function isCompleteEducationEntry(education) {
  return (
    hasText(education?.institution) &&
    hasText(education?.degree) &&
    (hasText(education?.from) || hasText(education?.to))
  );
}

function isValidEmail(value) {
  return hasText(value) && EMAIL_REGEX.test(value.trim());
}

function isValidPhone(value) {
  return hasText(value) && PHONE_REGEX.test(value.trim());
}

function isValidExperienceValue(value) {
  const normalizedValue = String(value ?? "").trim();

  return (
    normalizedValue.length > 0 && NON_NEGATIVE_NUMBER_REGEX.test(normalizedValue)
  );
}

function isPlaceholderNoise(value) {
  return hasText(value) && PLACEHOLDER_NOISE_REGEX.test(String(value).trim());
}

function isValidSocialUrl(value, platform) {
  if (!hasText(value)) return false;
  if (isPlaceholderNoise(value)) return false;

  const normalizedValue = String(value).trim();
  if (platform === "linkedin") return LINKEDIN_URL_REGEX.test(normalizedValue);
  if (platform === "github") return GITHUB_URL_REGEX.test(normalizedValue);

  return false;
}

function parseExperienceValue(value) {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue || !NON_NEGATIVE_NUMBER_REGEX.test(normalizedValue)) {
    return null;
  }

  const parsedValue = Number.parseFloat(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function hasMoreThanFourYearsExperience(resume) {
  const relevantExperience = parseExperienceValue(resume?.relevantExperience);
  const totalExperience = parseExperienceValue(resume?.totalExperience);

  return (
    (Number.isFinite(relevantExperience) && relevantExperience > 4) ||
    (Number.isFinite(totalExperience) && totalExperience > 4)
  );
}

function dedupeIssues(issues) {
  return Array.from(new Set(issues));
}

export function getSelectedResumeType() {
  if (typeof window === "undefined") return null;

  const experienceLevel = String(
    window.sessionStorage?.getItem("experienceLevel") || "",
  )
    .trim()
    .toLowerCase();

  if (experienceLevel === "fresher") return "fresher";
  if (experienceLevel === "experience" || experienceLevel === "experienced") {
    return "experienced";
  }

  return null;
}

function shouldRequireExperienceYears(resume) {
  const selectedResumeType = getSelectedResumeType();

  if (selectedResumeType === "fresher") return false;
  if (selectedResumeType === "experienced") return true;

  return getResumeExperienceLevel(resume) === "experienced";
}

function buildIntroValidationMessage(resume) {
  const missing = getMissingIntroFields(resume);
  const invalid = getInvalidIntroFields(resume);
  const messages = [];

  if (missing.length > 0) {
    messages.push(`Please complete: ${missing.join(", ")}`);
  }

  if (invalid.length > 0) {
    messages.push(`Please enter valid values for: ${invalid.join(", ")}`);
  }

  return messages.join(". ");
}

export function hasRequiredIntroFields(resume) {
  return (
    getMissingIntroFields(resume).length === 0 &&
    getInvalidIntroFields(resume).length === 0
  );
}

export function getMissingIntroFields(resume) {
  const missing = [];
  const requireExperienceYears = shouldRequireExperienceYears(resume);

  if (!hasText(resume?.name)) missing.push("Full Name");
  if (!hasText(resume?.roleTitle || resume?.role)) missing.push("Role / Title");
  if (!hasText(resume?.email)) missing.push("Email");
  if (!hasText(resume?.phone)) missing.push("Phone");
  if (!hasText(resume?.location)) missing.push("Location");
  if (
    requireExperienceYears &&
    !hasText(String(resume?.relevantExperience ?? ""))
  ) {
    missing.push("Relevant Experience");
  }
  if (
    requireExperienceYears &&
    !hasText(String(resume?.totalExperience ?? ""))
  ) {
    missing.push("Total Experience");
  }
  if (!hasText(resume?.summary)) missing.push("About Me");
  if (!hasMoreThanFourYearsExperience(resume) && !hasText(resume?.objective)) {
    missing.push("Career Objective");
  }

  return missing;
}

export function getInvalidIntroFields(resume) {
  const invalid = [];

  if (hasText(resume?.email) && !isValidEmail(resume.email)) {
    invalid.push("Email");
  }

  if (hasText(resume?.phone) && !isValidPhone(resume.phone)) {
    invalid.push("Phone");
  }

  if (
    hasText(String(resume?.relevantExperience ?? "")) &&
    !isValidExperienceValue(resume?.relevantExperience)
  ) {
    invalid.push("Relevant Experience");
  }

  if (
    hasText(String(resume?.totalExperience ?? "")) &&
    !isValidExperienceValue(resume?.totalExperience)
  ) {
    invalid.push("Total Experience");
  }

  return invalid;
}

export function hasAnySocialLink(resume) {
  return (
    isValidSocialUrl(resume?.linkedin, "linkedin") ||
    isValidSocialUrl(resume?.github, "github")
  );
}

export function getInvalidSocialFields(resume) {
  const invalid = [];

  if (hasText(resume?.linkedin) && !isValidSocialUrl(resume.linkedin, "linkedin")) {
    invalid.push("LinkedIn Profile URL");
  }

  if (hasText(resume?.github) && !isValidSocialUrl(resume.github, "github")) {
    invalid.push("GitHub Profile URL");
  }

  return invalid;
}

export function getEmptySkillEntries(resume) {
  return getEmptyTextSlots(resume?.skillGroups?.skills);
}

export function hasFilledSkills(resume) {
  return getFilledTextList(resume?.skillGroups?.skills).length > 0;
}

export function getIncompleteExperienceEntries(resume) {
  if (!Array.isArray(resume?.experience)) return [];

  return resume.experience.filter(
    (experience) =>
      hasStartedExperienceEntry(experience) &&
      !isCompleteExperienceEntry(experience)
  );
}

export function hasFilledExperience(resume) {
  return (
    Array.isArray(resume?.experience) &&
    resume.experience.some((experience) => isCompleteExperienceEntry(experience))
  );
}

export function getResumeExperienceLevel(resume) {
  const relevantExperience = parseExperienceValue(resume?.relevantExperience);
  const totalExperience = parseExperienceValue(resume?.totalExperience);

  if (
    (Number.isFinite(relevantExperience) && relevantExperience > 0) ||
    (Number.isFinite(totalExperience) && totalExperience > 0) ||
    hasFilledExperience(resume)
  ) {
    return "experienced";
  }

  return "fresher";
}

export function getIncompleteEducationEntries(resume) {
  if (!Array.isArray(resume?.education)) return [];

  return resume.education.filter(
    (education) =>
      hasStartedEducationEntry(education) &&
      !isCompleteEducationEntry(education)
  );
}

export function getEmptyLanguageEntries(resume) {
  return getEmptyTextSlots(resume?.languages);
}

export function hasFilledEducation(resume) {
  return (
    Array.isArray(resume?.education) &&
    resume.education.some((education) => isCompleteEducationEntry(education))
  );
}

export function getMissingRequiredFields(resume) {
  const issues = [
    ...getMissingIntroFields(resume),
    ...getInvalidIntroFields(resume),
    ...getInvalidSocialFields(resume),
  ];

  if (!hasAnySocialLink(resume)) {
    issues.push("LinkedIn or GitHub");
  }

  if (getIncompleteExperienceEntries(resume).length > 0) {
    issues.push("Complete Experience Entries");
  } else if (!hasFilledExperience(resume)) {
    issues.push("Experience");
  }

  if (getEmptySkillEntries(resume).length > 0) {
    issues.push("Complete Skill Entries");
  } else if (!hasFilledSkills(resume)) {
    issues.push("Skills");
  }

  if (getIncompleteEducationEntries(resume).length > 0) {
    issues.push("Complete Education Entries");
  } else if (!hasFilledEducation(resume)) {
    issues.push("Education");
  }

  if (getEmptyLanguageEntries(resume).length > 0) {
    issues.push("Complete Language Entries");
  }

  return dedupeIssues(issues);
}

export function getStepValidationError(stepKey, resume) {
  if (stepKey === "intro") {
    return buildIntroValidationMessage(resume) || null;
  }

  if (stepKey === "social") {
    const invalidSocial = getInvalidSocialFields(resume);
    if (invalidSocial.length > 0) {
      return `Please enter valid values for: ${invalidSocial.join(", ")}.`;
    }

    if (!hasAnySocialLink(resume)) {
      return "Please add at least one valid LinkedIn or GitHub link.";
    }
  }

  if (stepKey === "experience") {
    if (getIncompleteExperienceEntries(resume).length > 0) {
      return "Please complete or remove unfinished experience entries.";
    }

    if (!hasFilledExperience(resume)) {
      return "Please add one experience with company, role, duration, and description.";
    }
  }

  if (stepKey === "skills") {
    if (getEmptySkillEntries(resume).length > 0) {
      return "Please fill or remove empty skill rows.";
    }

    if (!hasFilledSkills(resume)) {
      return "Please add at least one skill.";
    }
  }

  if (stepKey === "education") {
    if (getIncompleteEducationEntries(resume).length > 0) {
      return "Please complete or remove unfinished education entries.";
    }

    if (getEmptyLanguageEntries(resume).length > 0) {
      return "Please fill or remove empty language rows.";
    }

    if (!hasFilledEducation(resume)) {
      return "Please add one education entry with institution, degree, and duration.";
    }
  }

  return null;
}
